import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAddAiChatMessageFeedbackMutation } from "@/lib/graphql/types-and-hooks";
import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";

interface Props {
  /**
   * Persisted ai_chat_message id. Undefined in preview mode or before the
   * assistant message has been saved — in that case feedback is toast-only.
   */
  messageId?: number;
  /** Existing vote loaded from history: 1 = up, -1 = down. */
  initialVote?: number | null;
  size?: "sm" | "md";
}

export default function AskCoffeeFeedback({ messageId, initialVote = null, size = "sm" }: Props) {
  const [vote, setVote] = useState<"up" | "down" | null>(
    initialVote === 1 ? "up" : initialVote === -1 ? "down" : null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const { t } = useTranslation();

  const workspaceId = useWorkspaceId();
  const [addFeedback] = useAddAiChatMessageFeedbackMutation();

  const iconClass = size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";
  const btnClass =
    "text-muted-foreground hover:text-foreground transition px-1 py-0.5 rounded hover:bg-muted/50";

  const submitVote = async (
    voteValue: 1 | -1,
    comment?: string,
    opts: { silent?: boolean } = {},
  ) => {
    const success = () => { if (!opts.silent) toast.success(t("askCoffee.feedbackThanks")); };

    // Preview mode, or message not yet persisted: optimistic toast, no network call.
    if (isLovablePreview() || messageId == null) {
      success();
      return;
    }

    try {
      await addFeedback({
        variables: {
          input: {
            workspaceId: String(workspaceId ?? ""),
            messageId,
            vote: voteValue,
            comment: comment?.trim() || undefined,
          },
        },
      });
      success();
    } catch (err) {
      toast.error((err as Error)?.message || t("askCoffee.feedbackFailed"));
    }
  };

  const handleUp = () => {
    if (vote === "up") return;
    setVote("up");
    submitVote(1);
  };

  const handleDown = () => {
    setVote("down");
    // Record the down vote immediately so it persists even if the comment
    // dialog is dismissed; the comment (if added) updates it on send.
    submitVote(-1, undefined, { silent: true });
    setDialogOpen(true);
  };

  const handleSend = () => {
    setDialogOpen(false);
    const comment = feedback;
    setFeedback("");
    submitVote(-1, comment);
  };

  return (
    <>
      <button
        onClick={handleUp}
        aria-label={t("askCoffee.goodResponse")}
        title={t("askCoffee.goodResponse")}
        className={btnClass}
        data-message-id={messageId}
      >
        <ThumbsUp
          className={cn(iconClass, vote === "up" && "fill-accent text-accent")}
        />
      </button>
      <button
        onClick={handleDown}
        aria-label={t("askCoffee.badResponse")}
        title={t("askCoffee.badResponse")}
        className={btnClass}
      >
        <ThumbsDown
          className={cn(iconClass, vote === "down" && "fill-accent text-accent")}
        />
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("askCoffee.feedbackTitle")}</DialogTitle>
            <DialogDescription>{t("askCoffee.feedbackDescription")}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t("askCoffee.feedbackPlaceholder")}
            rows={5}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSend} disabled={!feedback.trim()}>
              {t("askCoffee.sendFeedback")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
